import { hypotenuse2D } from '@paragon/utilities';

import Direction2D from './Direction2D';
import Point2D, { Point2DInterface } from './Point2D';
import Vector3D from './Vector3D';

export interface Vector2DInterface {
  readonly x: number;
  readonly y: number;
}

class Vector2D {
  static readonly zero = new Vector2D(0, 0);

  readonly x: number;
  readonly y: number;

  get imageOfOrigin() {
    return new Point2D(this.x, this.y);
  }

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  static fromDirectionAndMagnitude(direction: Direction2D, magnitude: number) {
    return new Vector2D(direction.x * magnitude, direction.y * magnitude);
  }

  static fromPointAndPoint(basePoint: Point2DInterface, endPoint: Point2DInterface) {
    return new Vector2D(endPoint.x - basePoint.x, endPoint.y - basePoint.y);
  }

  static fromOriginTo(endPoint: Point2DInterface) {
    return new Vector2D(endPoint.x, endPoint.y);
  }

  get direction() {
    return new Direction2D(this.x, this.y);
  }

  get magnitude() {
    return hypotenuse2D(this.x, this.y);
  }

  getComponentInDirection(direction: Direction2D) {
    return direction.x * this.x + direction.y * this.y;
  }

  dotProduct(other: Vector2D) {
    return this.x * other.x + this.y * other.y;
  }

  crossProduct(other: Vector2D) {
    return this.x * other.y - other.x * this.y;
  }

  multiplyScalar(scalar: number) {
    return new Vector2D(this.x * scalar, this.y * scalar);
  }

  divideScalar(scalar: number) {
    return new Vector2D(this.x / scalar, this.y / scalar);
  }

  add(vector: Vector2DInterface) {
    return new Vector2D(this.x + vector.x, this.y + vector.y);
  }

  subtract(vector: Vector2DInterface) {
    return new Vector2D(this.x - vector.x, this.y - vector.y);
  }

  reverse() {
    return new Vector2D(-this.x, -this.y);
  }

  to3D(z: number = 0) {
    return new Vector3D(this.x, this.y, z);
  }
}

export default Vector2D;
